// src/components/ServerDatabase/ServerFilterToolbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const ServerFilterToolbar = ({
  versionInput,
  onVersionChange,
  playersInput,
  onPlayersChange,
  timeFilter,
  onTimeFilterChange,
  onResetAll,
  sortBy,
  onSortChange,
  viewMode,
  onViewModeChange
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const [timeMenuOpen, setTimeMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  const timeRef = useRef(null);
  const sortRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (timeRef.current && !timeRef.current.contains(e.target)) {
        setTimeMenuOpen(false);
      }
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setSortMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const timeOptions = [
    { value: 'all', label: isEn ? 'Any time' : 'Любое время' },
    { value: '24h', label: isEn ? 'Last 24 hours' : 'За 24 часа' },
    { value: '7d', label: isEn ? 'Last 7 days' : 'За неделю' },
    { value: '30d', label: isEn ? 'Last 30 days' : 'За 30 дней' }
  ];

  const sortOptions = [
    { value: 'online-desc', label: isEn ? 'By online (desc)' : 'По онлайну (убыв.)' },
    { value: 'online-asc', label: isEn ? 'By online (asc)' : 'По онлайну (возр.)' },
    { value: 'recent', label: isEn ? 'By audit date' : 'По дате теста' },
    { value: 'name', label: isEn ? 'By address (A-Z)' : 'По адресу (А-Я)' },
    { value: 'ping', label: isEn ? 'By ping' : 'По пингу' }
  ];

  const currentTimeLabel = timeOptions.find(o => o.value === timeFilter)?.label || (isEn ? 'Any time' : 'Любое время');
  const currentSortLabel = sortOptions.find(o => o.value === sortBy)?.label || (isEn ? 'By online (desc)' : 'По онлайну (убыв.)');

  return (
    <section className="sdb-filter-dock-toolbar">
      {/* Левая группа: 2 инпута (Версия, Игроки) + дропдаун (Тестирование) + Сброс */}
      <div className="sdb-filter-controls-left">
        
        {/* Ряд инпутов: Версия и Игроки от */}
        <div className="sdb-filter-inputs-row">
          {/* 1. Поле ввода: Версия */}
          <label className="sdb-filter-input-pill" htmlFor="sdbFilterVersionInput">
            <span className="sdb-filter-pill-label-title">
              {isEn ? 'Version:' : 'Версия:'}
            </span>
            <input
              type="text"
              id="sdbFilterVersionInput"
              className="sdb-filter-pill-input"
              placeholder={isEn ? 'Any (1.20)' : 'Любая (1.20)'}
              autoComplete="off"
              spellCheck="false"
              value={versionInput}
              onChange={(e) => onVersionChange(e.target.value)}
            />
          </label>

          {/* 2. Поле ввода: Количество игроков */}
          <label className="sdb-filter-input-pill" htmlFor="sdbFilterPlayersInput">
            <span className="sdb-filter-pill-label-title">
              {isEn ? 'Players from:' : 'Игроков от:'}
            </span>
            <input
              type="number"
              id="sdbFilterPlayersInput"
              className="sdb-filter-pill-input sdb-filter-players-input"
              placeholder="0"
              min="0"
              step="50"
              autoComplete="off"
              value={playersInput || ''}
              onChange={(e) => onPlayersChange(e.target.value)}
            />
          </label>
        </div>

        {/* Ряд действий фильтра: Дропдаун тестирования + Компактный сброс */}
        <div className="sdb-filter-actions-row">
          {/* 3. Фильтр: Последнее тестирование */}
          <div ref={timeRef} className={`sdb-filter-pill-wrapper ${timeMenuOpen ? 'open' : ''}`}>
            <button
              type="button"
              className={`sdb-filter-pill-trigger ${timeFilter !== 'all' ? 'has-value' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setTimeMenuOpen(!timeMenuOpen);
                setSortMenuOpen(false);
              }}
            >
              <span className="sdb-filter-pill-label-dim">
                {isEn ? 'Audited:' : 'Тестирование:'}
              </span>
              <span>{currentTimeLabel}</span>
              <svg className="chevron" viewBox="0 0 24 24" fill="none">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            <div className="sdb-filter-dropdown-menu">
              {timeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`sdb-dropdown-option ${timeFilter === opt.value ? 'selected' : ''}`}
                  onClick={() => {
                    onTimeFilterChange(opt.value);
                    setTimeMenuOpen(false);
                  }}
                >
                  <span>{opt.label}</span>
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </button>
              ))}
            </div>
          </div>

          {/* Кнопка быстрой очистки фильтров */}
          <button
            type="button"
            className="sdb-filter-reset-pill"
            onClick={onResetAll}
            title={isEn ? 'Reset all filters' : 'Сбросить все параметры'}
          >
            <svg viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            <span>{isEn ? 'Reset' : 'Сброс'}</span>
          </button>
        </div>

      </div>

      {/* Правая группа: Сортировка + Переключатель Grid / Table */}
      <div className="sdb-filter-controls-right">
        
        {/* Сортировка */}
        <div ref={sortRef} className={`sdb-filter-pill-wrapper ${sortMenuOpen ? 'open' : ''}`}>
          <button
            type="button"
            className={`sdb-filter-pill-trigger ${sortBy !== 'online-desc' ? 'has-value' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setSortMenuOpen(!sortMenuOpen);
              setTimeMenuOpen(false);
            }}
          >
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, stroke: 'currentColor', strokeWidth: 2 }}>
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="7" y1="12" x2="17" y2="12" />
              <line x1="10" y1="18" x2="14" y2="18" />
            </svg>
            <span className="sdb-filter-pill-label-dim">
              {isEn ? 'Sort:' : 'Сортировка:'}
            </span>
            <span>{currentSortLabel}</span>
            <svg className="chevron" viewBox="0 0 24 24" fill="none">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <div className="sdb-filter-dropdown-menu" style={{ left: 'auto', right: 0 }}>
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`sdb-dropdown-option ${sortBy === opt.value ? 'selected' : ''}`}
                onClick={() => {
                  onSortChange(opt.value);
                  setSortMenuOpen(false);
                }}
              >
                <span>{opt.label}</span>
                <svg viewBox="0 0 24 24">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </button>
            ))}
          </div>
        </div>

        {/* Переключатель Сетка / Таблица */}
        <div className="sdb-view-segment-control">
          <button
            type="button"
            className={`sdb-view-segment-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => onViewModeChange('grid')}
            title={isEn ? 'Grid view' : 'Вид: Сетка карточек'}
          >
            <svg viewBox="0 0 24 24">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
            </svg>
          </button>
          <button
            type="button"
            className={`sdb-view-segment-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => onViewModeChange('table')}
            title={isEn ? 'Table view' : 'Вид: Таблица'}
          >
            <svg viewBox="0 0 24 24">
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
          </button>
        </div>

      </div>

    </section>
  );
};

export default ServerFilterToolbar;
